// Stable translation keys for the structured content in content/facts.ts (and the calculator's sourced reasons).
// English stays in the data file; translations live in locales/<code>/content.json under these keys.
// Never translated: source labels, official document/form titles (link names, grant names), S$ thresholds.
import {
  benefitStats, benefits, changelog, faq, foc, grants, helpLinks, inHouseConnect, irspCount, myTaxMail, phases, psg, totalAnnualSupplies, usefulLinks,
} from '../../content/facts'
import { bandReasons, reasons, windows } from '../calculator'

export interface ContentEntry { key: string; text: string; page: string }

const keyOf = new WeakMap<object, Map<string, string>>()
const entries: ContentEntry[] = []

function reg(obj: object, field: string, key: string, page: string) {
  const text = (obj as Record<string, unknown>)[field]
  if (typeof text !== 'string' || !text) return
  if (!keyOf.has(obj)) keyOf.set(obj, new Map())
  keyOf.get(obj)!.set(field, key)
  entries.push({ key, text, page })
}

const POS_FAQ = new Set(['pos-aggregated', 'partners-not-on-invoicenow', 'customer-not-registered', 'deadline', 'acknowledgement'])
/** FAQ entries also quoted on other pages (src/pages/Faq.tsx, Grants.tsx, Setup.tsx). */
const FAQ_ALSO: Record<string, string[]> = {
  'is-it-free': ['/grants'], 'software-ready': ['/grants', '/setup', '/free-software'], 'access-point': ['/grants'],
  'who-and-when': ['/when'], 'what-is-it': ['/setup', '/how-it-works'], acknowledgement: ['/pos-and-retail', '/setup'],
}

for (const f of faq) {
  const page = ['/faq', ...(POS_FAQ.has(f.id) ? ['/pos-and-retail'] : []), ...(FAQ_ALSO[f.id] ?? [])].filter((p, i, a) => a.indexOf(p) === i).join(',')
  reg(f, 'q', `faq.${f.id}.q`, page)
  f.claims.forEach((c, i) => reg(c, 'text', `faq.${f.id}.${i}`, page))
}
for (const p of phases) reg(p, 'who', `phase.${p.id}.who`, '/when')
reg(totalAnnualSupplies, 'text', 'when.totalAnnualSupplies', '/when')
reg(myTaxMail, 'text', 'when.myTaxMail', '/when,/setup')
for (const [id, [, c]] of Object.entries(windows)) reg(c, 'text', `calc.window.${id}`, '/when')
for (const [id, c] of Object.entries(reasons)) reg(c, 'text', `calc.reason.${id}`, '/when')
for (const [id, c] of Object.entries(bandReasons)) reg(c, 'text', `calc.reason.band_${id}`, '/when')
foc.claims.forEach((c, i) => reg(c, 'text', `foc.${i}`, '/free-software'))
for (const g of grants) {
  const page = g.id === 'foc' ? '/free-software' : '/grants'
  for (const f of ['amount', 'who', 'period'] as const) reg(g, f, `grant.${g.id}.${f}`, page)
  if (g.href) reg(g.href, 'label', `grant.${g.id}.hrefLabel`, page)
  g.terms.forEach((c, i) => { if (!keyOf.get(c)) reg(c, 'text', `grant.${g.id}.term.${i}`, page) })
}
reg(psg, 'text', 'grants.psg', '/grants')
reg(irspCount, 'text', 'software.irspCount', '/free-software')
reg(inHouseConnect, 'text', 'vendor.inHouseConnect', '/free-software,/setup')
benefits.forEach((b, i) => { reg(b, 'lead', `benefit.${i}.lead`, '/'); reg(b, 'text', `benefit.${i}.text`, '/') })
benefitStats.forEach((b, i) => { reg(b, 'lead', `benefitStat.${i}.lead`, '/'); reg(b, 'text', `benefitStat.${i}.text`, '/') })
usefulLinks.forEach((g, gi) => { reg(g, 'group', `links.${gi}.group`, '/links'); g.items.forEach((l, i) => reg(l, 'why', `links.${gi}.${i}.why`, '/links')) })
helpLinks.forEach((g, gi) => { reg(g, 'group', `help.${gi}.group`, '/'); g.items.forEach((l, i) => reg(l, 'why', `help.${gi}.${i}.why`, '/')) })
changelog.forEach((c, ci) => {
  // Keyed by date + position from the end, so adding a new entry at the top does not shift older keys.
  const id = `${c.date}.${changelog.length - 1 - ci}`
  reg(c, 'title', `change.${id}.title`, '/changelog')
  c.items.forEach((text, i) => entries.push({ key: `change.${id}.${i}`, text, page: '/changelog' }))
})

/** Every translatable content string with its key and the page(s) it appears on. */
export const contentEntries: ContentEntry[] = entries

/** The content key for a field of a data object, or undefined if it is not translatable. */
export const contentKey = (obj: object, field = 'text') => keyOf.get(obj)?.get(field)

/** Changelog items are plain strings; look their keys up by entry and index. */
export function changeItemKey(entry: (typeof changelog)[number], index: number) {
  const ci = changelog.indexOf(entry)
  return `change.${entry.date}.${changelog.length - 1 - ci}.${index}`
}
